<script setup lang="ts">
import type { IconKeys } from "../ui/Icon/Icon.interface"
import type { CreateRsvpResponseArgs } from "@/api"
import type { IRsvpAdditionalGuest, RsvpResponse } from "@/models"
import { toTypedSchema } from "@vee-validate/zod"
import { useField, useFieldArray, useForm } from "vee-validate"
import { computed, nextTick, ref, watchEffect } from "vue"
import z from "zod"
import { useSendRsvpResponse, useUpdateRsvpResponse } from "@/api"
import { Button, Expandable, Icon, Input, Textarea } from "@/components/ui"
import { useTranslation } from "@/composables"
import { RsvpStatus } from "@/models"

interface InvitationConfig {
  allowDietaryRequirements?: boolean
  allowSongRequests?: boolean
}

const props = defineProps<{
  mode: "guest" | "host"
  eventVaultId?: number
  guestResponse?: RsvpResponse
  invitation?: InvitationConfig
  guestId?: number
  accessToken?: string
  emailToken?: string
}>()

const emit = defineEmits<{
  (e: "close"): void
  (e: "success"): void
  (e: "error"): void
}>()

const { t } = useTranslation()

function isAdditionalGuestsEnabledStatus(status?: RsvpStatus) {
  return status === RsvpStatus.ATTENDING || status === RsvpStatus.MAYBE
}

const additionalGuestDraftSchema = z.object({
  eventGuestId: z.number().optional(),
  name: z.string().optional(),
  email: z.string().optional(),
  dietaryRequirements: z.string().optional(),
  songRequests: z.string().optional(),
  rsvpStatus: z.nativeEnum(RsvpStatus).optional(),
})

const additionalGuestSchema = z.object({
  eventGuestId: z.number().optional(),
  name: z.string().min(1, t("invitation_rsvp_form.validation.additional_guest_name_required")).max(28, t("invitation_rsvp_form.validation.name_max")),
  email: z.preprocess(
    val => (val ?? ""),
    z.union([z.literal(""), z.string().email(t("invitation_rsvp_form.validation.email_invalid")).max(50, t("invitation_rsvp_form.validation.email_max"))]),
  ).optional(),
  dietaryRequirements: z.string().optional(),
  songRequests: z.string().optional(),
  rsvpStatus: z.enum([RsvpStatus.ATTENDING, RsvpStatus.MAYBE, RsvpStatus.NOT_ATTENDING]),
})

const optionalEmailSchema = z.preprocess(
  val => (val ?? ""),
  z.union([z.literal(""), z.string().email(t("invitation_rsvp_form.validation.email_invalid")).max(50, t("invitation_rsvp_form.validation.email_max"))]),
).optional()

const requiredEmailSchema = z.preprocess(
  val => (val ?? ""),
  z.string().min(1, t("invitation_rsvp_form.validation.email_required")).email(t("invitation_rsvp_form.validation.email_invalid")).max(50, t("invitation_rsvp_form.validation.email_max")),
)

const validationSchema = toTypedSchema(
  z.object({
    name: z.string().min(1, t("invitation_rsvp_form.validation.name_required")).max(28, t("invitation_rsvp_form.validation.name_max")),
    email: props.mode === "host" ? optionalEmailSchema : requiredEmailSchema,
    status: z.enum([RsvpStatus.ATTENDING, RsvpStatus.MAYBE, RsvpStatus.NOT_ATTENDING]),
    comment: z.string().max(500, t("invitation_rsvp_form.validation.comment_max")).optional(),
    dietaryRequirements: z.string().optional(),
    songRequest: z.string().optional(),
    additionalGuests: z.array(additionalGuestDraftSchema),
  }).superRefine((values, ctx) => {
    if (!isAdditionalGuestsEnabledStatus(values.status))
      return

    values.additionalGuests.forEach((guest, index) => {
      const result = additionalGuestSchema.safeParse(guest)

      if (!result.success) {
        result.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ["additionalGuests", index, ...issue.path],
          })
        })
      }
    })
  }),
)

const { handleSubmit, values, errors, setFieldValue } = useForm({
  validationSchema,
  initialValues: {
    additionalGuests: [],
  },
})

const { value: statusValue, handleChange: handleStatusChange } = useField<RsvpStatus>("status")
const { fields: additionalGuestFields, push, remove } = useFieldArray<IRsvpAdditionalGuest>("additionalGuests")

const emailTokenRef = computed(() => props.mode === "guest" ? props.emailToken : undefined)
const guestIdRef = computed(() => props.mode === "host" ? props.guestId : undefined)

const { mutate: createRsvpResponse, isPending: isCreatingRsvpResponse } = useSendRsvpResponse(props.eventVaultId, props.accessToken)
const { mutate: updateRsvpResponse, isPending: isUpdatingRsvpResponse } = useUpdateRsvpResponse(props.eventVaultId, emailTokenRef, guestIdRef)

const isExistingGuest = computed(() => Boolean(props.guestResponse?.eventGuestId))
const canManageAdditionalGuests = computed(() => isAdditionalGuestsEnabledStatus(values.status))
const namePlaceholder = computed(() => props.mode === "host" ? t("invitation_rsvp_form.placeholder_host_full_name") : t("invitation_rsvp_form.placeholder_full_name"))
const emailLabel = computed(() => props.mode === "host" ? t("invitation_rsvp_form.label_host_email_address") : t("invitation_rsvp_form.label_email_address"))
const emailPlaceholder = computed(() => props.mode === "host" ? t("invitation_rsvp_form.placeholder_host_email_address") : t("invitation_rsvp_form.placeholder_email_address"))

const isSubmitting = computed(() => isCreatingRsvpResponse.value || isUpdatingRsvpResponse.value)
const additionalGuestNameInputs = ref<Array<{ inputRef: HTMLInputElement | null } | null>>([])

function getPlainAdditionalGuests(additionalGuests: IRsvpAdditionalGuest[]) {
  return additionalGuests.map(guest => ({
    eventGuestId: guest.eventGuestId,
    name: guest.name ?? "",
    email: guest.email ?? "",
    dietaryRequirements: guest.dietaryRequirements,
    songRequests: guest.songRequests,
    rsvpStatus: guest.rsvpStatus ?? RsvpStatus.ATTENDING,
  }))
}

function getAdditionalGuestStatusFieldName(index: number): `additionalGuests[${number}].rsvpStatus` {
  return `additionalGuests[${index}].rsvpStatus`
}

function getAdditionalGuestDefaults(rsvpStatus?: RsvpStatus): IRsvpAdditionalGuest {
  return {
    name: "",
    email: "",
    dietaryRequirements: "",
    songRequests: "",
    rsvpStatus: rsvpStatus ?? RsvpStatus.ATTENDING,
  }
}

function normaliseAdditionalGuest(guest: IRsvpAdditionalGuest, rsvpStatus?: RsvpStatus): IRsvpAdditionalGuest {
  return {
    ...getAdditionalGuestDefaults(rsvpStatus),
    ...guest,
    rsvpStatus: guest.rsvpStatus ?? rsvpStatus ?? RsvpStatus.ATTENDING,
  }
}

const onSubmit = handleSubmit((values) => {
  const additionalGuests = canManageAdditionalGuests.value
    ? getPlainAdditionalGuests((values.additionalGuests ?? []).map(guest => normaliseAdditionalGuest(guest as IRsvpAdditionalGuest, values.status)))
    : []

  const rsvpResponse: CreateRsvpResponseArgs = {
    name: values.name,
    additionalGuests,
    rsvpDetails: {
      rsvpStatus: values.status,
      guestRSVPComment: values.comment,
      dietaryRequirements: values.dietaryRequirements,
      songRequests: values.songRequest,
      guestCount: additionalGuests.length + 1,
    },
  }

  const email = values.email?.trim()

  if (email) {
    rsvpResponse.email = email
  }

  if (isExistingGuest.value) {
    updateRsvpResponse(rsvpResponse, {
      onSuccess: async () => emit("success"),
      onError: () => emit("error"),
    })
    return
  }

  createRsvpResponse(rsvpResponse, {
    onSuccess: async () => emit("success"),
    onError: () => emit("error"),
  })
})

function setAdditionalGuestNameInputRef(index: number, input: unknown) {
  additionalGuestNameInputs.value[index] = input as { inputRef: HTMLInputElement | null } | null
}

async function addAdditionalGuest() {
  const newIndex = values.additionalGuests?.length ?? 0
  push(getAdditionalGuestDefaults(values.status))
  await nextTick()
  additionalGuestNameInputs.value[newIndex]?.inputRef?.focus()
}

function removeAdditionalGuest(index: number) {
  additionalGuestNameInputs.value.splice(index, 1)
  remove(index)
}

function handleAdditionalGuestStatusChange(index: number, event: Event) {
  const target = event.target as HTMLInputElement
  setFieldValue(getAdditionalGuestStatusFieldName(index), target.value as never)
}

function getFallbackAdditionalGuests(guestResponse: RsvpResponse): IRsvpAdditionalGuest[] {
  if (guestResponse.additionalGuests?.length)
    return guestResponse.additionalGuests.map(guest => normaliseAdditionalGuest(guest, guestResponse.rsvpDetails?.rsvpStatus))

  const guestCount = guestResponse.rsvpDetails?.guestCount ?? 1

  return Array.from({ length: Math.max(guestCount - 1, 0) }, () => getAdditionalGuestDefaults(guestResponse.rsvpDetails?.rsvpStatus))
}

const isFormInitialised = ref(false)

async function initialiseFormValues(guestResponse: RsvpResponse) {
  if (guestResponse.name)
    setFieldValue("name", guestResponse.name)

  if (guestResponse.email)
    setFieldValue("email", guestResponse.email)

  if (guestResponse.rsvpDetails?.guestRSVPComment)
    setFieldValue("comment", guestResponse.rsvpDetails.guestRSVPComment)

  if (guestResponse.rsvpDetails?.dietaryRequirements)
    setFieldValue("dietaryRequirements", guestResponse.rsvpDetails.dietaryRequirements)

  if (guestResponse.rsvpDetails?.songRequests)
    setFieldValue("songRequest", guestResponse.rsvpDetails.songRequests)

  setFieldValue("additionalGuests", getFallbackAdditionalGuests(guestResponse))

  if (guestResponse.rsvpDetails?.rsvpStatus) {
    const status = guestResponse.rsvpDetails.rsvpStatus
    if (status === RsvpStatus.ATTENDING || status === RsvpStatus.MAYBE || status === RsvpStatus.NOT_ATTENDING)
      setFieldValue("status", status)
  }

  isFormInitialised.value = true
}

watchEffect(() => {
  if (props.guestResponse && !isFormInitialised.value)
    initialiseFormValues(props.guestResponse)
})

const rsvpStatusOptions: { value: RsvpStatus, label: string, icon: IconKeys }[] = [
  { value: RsvpStatus.ATTENDING, label: t("invitation_rsvp_form.status_options.attending"), icon: "Check" },
  { value: RsvpStatus.NOT_ATTENDING, label: t("invitation_rsvp_form.status_options.unable_to_attend"), icon: "Close" },
  { value: RsvpStatus.MAYBE, label: t("invitation_rsvp_form.status_options.maybe"), icon: "Help" },
]
</script>

<template>
  <div class="p-4 md:p-8 md:min-h-120">
    <button
      v-if="mode === 'guest'"
      type="button"
      class="mb-6 flex items-center gap-2 text-secondary-card-text hover:text-primary transition-colors cursor-pointer"
      :disabled="isSubmitting"
      @click="emit('close')"
    >
      <Icon icon="ArrowLeft" />
      <span class="font-medium">{{ t("invitation_rsvp_form.back_button") }}</span>
    </button>
    <form class="max-w-4xl mx-auto flex flex-col gap-6 md:gap-8" @submit="onSubmit">
      <div v-if="mode === 'guest'" class="text-center mb-8">
        <h2 class="text-3xl font-comfortaa font-bold mb-2">
          {{ t("invitation_rsvp_form.rsvp_title") }}
        </h2>
        <p class="text-secondary-card-text">
          {{ t("invitation_rsvp_form.rsvp_description") }}
        </p>
      </div>

      <div class="grid md:grid-cols-2 gap-6">
        <!-- full name -->
        <Input
          :disabled="isSubmitting"
          name="name"
          :label="t('invitation_rsvp_form.label_full_name')"
          :placeholder="namePlaceholder"
        />

        <!-- email address -->
        <Input
          :disabled="isSubmitting"
          name="email"
          :label="emailLabel"
          :placeholder="emailPlaceholder"
        />
      </div>

      <!-- rsvp status -->
      <div class="flex flex-col gap-2">
        <label class="block text-sm font-semibold font-montserrat">
          {{ t("invitation_rsvp_form.label_will_you_attend") }}
        </label>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label
            v-for="option in rsvpStatusOptions"
            :key="option.value"
            class="relative flex items-center justify-center gap-3 py-2 sm:py-4 rounded-lg cursor-pointer transition-all bg-secondary-card border-2 focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2"
            :class="statusValue === option.value ? `bg-primary text-primary-btn-text` : 'border-secondary-card-text'"
          >
            <input
              :disabled="isSubmitting"
              :checked="statusValue === option.value"
              type="radio"
              name="status"
              :value="option.value"
              class="sr-only"
              @change="handleStatusChange"
              @keyup.space.prevent="handleStatusChange"
            >
            <Icon :icon="option.icon" />
            <span class="font-semibold">{{ option.label }}</span>
          </label>
        </div>
        <span v-if="errors.status" class="gpx-field-error" v-text="errors.status" />
      </div>

      <div v-if="canManageAdditionalGuests && (invitation?.allowDietaryRequirements || invitation?.allowSongRequests)" class="flex flex-col gap-6 md:gap-8">
        <!-- dietary requirements -->
        <Textarea
          v-if="invitation?.allowDietaryRequirements"
          :disabled="isSubmitting"
          name="dietaryRequirements"
          :label="t('invitation_rsvp_form.label_dietary_requirements')"
          :placeholder="t('invitation_rsvp_form.placeholder_dietary_requirements')"
        />

        <!-- song request -->
        <Input
          v-if="invitation?.allowSongRequests"
          :disabled="isSubmitting"
          name="songRequest"
          :label="t('invitation_rsvp_form.label_song_request')"
          :placeholder="t('invitation_rsvp_form.placeholder_song_request')"
        />
      </div>

      <!-- additional comments -->
      <Textarea
        :disabled="isSubmitting"
        name="comment"
        :label="t('invitation_rsvp_form.label_additional_comments')"
        :placeholder="t('invitation_rsvp_form.placeholder_additional_comments')"
      />

      <div class="w-full">
        <div v-if="canManageAdditionalGuests" class="flex flex-col gap-4 md:gap-6">
          <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div class="flex flex-col gap-1 text-left">
              <h3 class="font-comfortaa text-lg font-bold">
                {{ t("invitation_rsvp_form.additional_guests_title") }}
              </h3>
              <p class="text-sm text-secondary-card-text">
                {{ t("invitation_rsvp_form.additional_guests_description") }}
              </p>
            </div>
            <Button type="button" variant="secondary" @click="addAdditionalGuest">
              <template #prefixIcon>
                <Icon icon="CirclePlus" size="sm" />
              </template>
              {{ t("invitation_rsvp_form.add_additional_guest") }}
            </Button>
          </div>

          <div v-if="additionalGuestFields.length" class="flex flex-col gap-4 border-t border-gray-500 pt-4 md:gap-6 md:pt-6">
            <Expandable v-for="(field, index) in additionalGuestFields" :key="field.key" :initial-open="true">
              <template #activator="{ toggle, isOpen, activatorAttrs }">
                <div class="flex items-center justify-between gap-4 rounded-lg bg-secondary-card/40 px-4 py-3">
                  <h4 class="font-comfortaa text-base font-bold">
                    {{ t("invitation_rsvp_form.additional_guest_label", { number: index + 2 }) }}
                  </h4>
                  <div class="flex items-center gap-2">
                    <Button
                      v-bind="activatorAttrs"
                      variant="secondary"
                      :data-test="`additional-guest-toggle-${index}`"
                      @click="toggle"
                    >
                      <span class="sr-only md:not-sr-only">
                        {{ isOpen ? t("invitation_rsvp_form.additional_guest_toggle_hide") : t("invitation_rsvp_form.additional_guest_toggle_show") }}
                      </span>
                      <template #suffixIcon>
                        <Icon icon="CircleChevronDown" size="sm" class="transition-transform duration-200 ease-in-out" :class="{ 'rotate-180': isOpen }" />
                      </template>
                      <span class="sr-only">{{ t("invitation_rsvp_form.additional_guest_toggle_sr", { number: index + 2, state: isOpen ? t('invitation_rsvp_form.additional_guest_toggle_hide') : t('invitation_rsvp_form.additional_guest_toggle_show') }) }}</span>
                    </Button>
                    <Button type="button" variant="danger" @click="removeAdditionalGuest(index)">
                      <template #prefixIcon>
                        <Icon icon="Trash" size="sm" />
                      </template>
                      <span class="sr-only md:not-sr-only">
                        {{ t("invitation_rsvp_form.remove_additional_guest") }}
                      </span>
                    </Button>
                  </div>
                </div>
              </template>

              <div class="flex flex-col gap-4 px-1 pt-4">
                <div class="grid gap-4 md:grid-cols-2 md:gap-6">
                  <Input
                    :ref="input => setAdditionalGuestNameInputRef(index, input)"
                    :disabled="isSubmitting"
                    :name="`additionalGuests.${index}.name`"
                    :label="t('invitation_rsvp_form.label_additional_guest_name')"
                    :placeholder="t('invitation_rsvp_form.placeholder_additional_guest_name')"
                  />
                  <Input
                    :disabled="isSubmitting"
                    :name="`additionalGuests.${index}.email`"
                    :label="t('invitation_rsvp_form.label_additional_guest_email')"
                    :placeholder="t('invitation_rsvp_form.placeholder_additional_guest_email')"
                  />
                </div>

                <div class="flex flex-col gap-2">
                  <label class="block text-sm font-semibold font-montserrat">
                    {{ t("invitation_rsvp_form.label_additional_guest_will_attend") }}
                  </label>
                  <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <label
                      v-for="option in rsvpStatusOptions"
                      :key="`${field.key}-${option.value}`"
                      class="relative flex items-center justify-center gap-3 rounded-lg border-2 bg-secondary-card px-4 py-2 transition-all focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2"
                      :class="values.additionalGuests?.[index]?.rsvpStatus === option.value ? 'bg-primary text-primary-btn-text' : 'border-secondary-card-text'"
                    >
                      <input
                        :disabled="isSubmitting"
                        :checked="values.additionalGuests?.[index]?.rsvpStatus === option.value"
                        type="radio"
                        :name="`additionalGuests.${index}.rsvpStatus`"
                        :value="option.value"
                        class="sr-only"
                        @change="event => handleAdditionalGuestStatusChange(index, event)"
                        @keyup.space.prevent="event => handleAdditionalGuestStatusChange(index, event)"
                      >
                      <Icon :icon="option.icon" class="shrink-0" />
                      <span class="font-semibold">{{ option.label }}</span>
                    </label>
                  </div>
                  <span v-if="errors[`additionalGuests[${index}].rsvpStatus` as const]" class="gpx-field-error" v-text="errors[`additionalGuests[${index}].rsvpStatus` as const]" />
                </div>

                <div v-if="isAdditionalGuestsEnabledStatus(values.additionalGuests?.[index]?.rsvpStatus) && (invitation?.allowDietaryRequirements || invitation?.allowSongRequests)" class="flex flex-col gap-4 md:gap-6">
                  <Textarea
                    v-if="invitation?.allowDietaryRequirements"
                    :disabled="isSubmitting"
                    :name="`additionalGuests.${index}.dietaryRequirements`"
                    :label="t('invitation_rsvp_form.label_additional_guest_dietary_requirements')"
                    :placeholder="t('invitation_rsvp_form.placeholder_additional_guest_dietary_requirements')"
                  />
                  <Input
                    v-if="invitation?.allowSongRequests"
                    :disabled="isSubmitting"
                    :name="`additionalGuests.${index}.songRequests`"
                    :label="t('invitation_rsvp_form.label_additional_guest_song_request')"
                    :placeholder="t('invitation_rsvp_form.placeholder_additional_guest_song_request')"
                  />
                </div>
              </div>
            </Expandable>
          </div>

          <div v-if="additionalGuestFields.length" class="flex justify-end">
            <Button type="button" variant="secondary" @click="addAdditionalGuest">
              <template #prefixIcon>
                <Icon icon="CirclePlus" size="sm" />
              </template>
              {{ t("invitation_rsvp_form.add_additional_guest") }}
            </Button>
          </div>
        </div>
      </div>

      <!-- submit button -->
      <div class="flex flex-col items-center gap-4 pt-4">
        <Button type="submit" size="lg" class="w-full sm:w-fit sm:px-12 sm:py-4" :disabled="isSubmitting">
          <template #prefixIcon>
            <Icon icon="PaperPlane" size="sm" />
          </template>
          {{ isSubmitting ? t("invitation_rsvp_form.submitting") : Boolean(guestResponse?.rsvpDetails) ? t("invitation_rsvp_form.update_response") : t("invitation_rsvp_form.submit_button") }}
          <template #suffixIcon>
            <Icon v-if="isSubmitting" icon="Loading" />
          </template>
        </Button>
      </div>
    </form>
  </div>
</template>
